import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DirectoriesApiService } from '@directory-of-directories/domain';
import { combineLatest, forkJoin, map, shareReplay, startWith, switchMap } from 'rxjs';
import { DirectoryToolbarStateService } from './directory-toolbar-state.service';

@Injectable({ providedIn: 'root' })
export class DirectoryStoreService {
  private readonly _directoriesService = inject(DirectoriesApiService);
  private readonly _toolbarState = inject(DirectoryToolbarStateService);

  private readonly DEFAULT_META = {
    current_page: 0,
    total_pages: 0,
    page_size: 0,
    total_count: 0,
  };

  private readonly GHOSTING_LIST = Array(10).fill(null);

  private readonly initialState = {
    loading: true,
    us: {
      spotlight_directories: [...this.GHOSTING_LIST],
      meta: this.DEFAULT_META,
    },
    eu: {
      spotlight_directories: [...this.GHOSTING_LIST],
      meta: this.DEFAULT_META,
    },
  };

  private readonly directoriesWithLoading$ = this._toolbarState.urlState$.pipe(
    switchMap(({ sorting, search, skill_categories, us, eu }) =>
      forkJoin([
        this._directoriesService.loadDirectories('US', sorting, search, skill_categories, us.page),
        this._directoriesService.loadDirectories('EU', sorting, search, skill_categories, eu.page),
      ]).pipe(
        map(([us, eu]) => ({ loading: false, us, eu })),
        startWith(this.initialState),
      ),
    ),
    shareReplay(1),
  );

  private readonly _currentRegion = toSignal(this._toolbarState.currentRegion$, {
    initialValue: 'US',
  });

  readonly directories = toSignal(
    this.directoriesWithLoading$.pipe(
      map(({ us, eu }) => ({
        us: {
          data: us.spotlight_directories,
          total_count: us.meta?.total_count ?? 0,
        },
        eu: {
          data: eu.spotlight_directories,
          total_count: eu.meta?.total_count ?? 0,
        },
      })),
    ),
  );

  readonly currentRegionMetadata = toSignal(
    combineLatest([this.directoriesWithLoading$, this._toolbarState.currentRegion$]).pipe(
      map(([{ us, eu }, region]) => (region === 'US' ? us.meta : eu.meta)),
    ),
    { initialValue: this.DEFAULT_META },
  );

  readonly loading = toSignal(this.directoriesWithLoading$.pipe(map(({ loading }) => loading)));

  readonly noResults = computed(() => {
    if (this.loading()) return false;
    return this._currentRegion() === 'US'
      ? this.directories().us.data.length === 0
      : this.directories().eu.data.length === 0;
  });
}
